import * as THREE from 'three';
import { applySvgColorOverrides, decodeSvgDataUrl, encodeSvgDataUrl } from '@/lib/designState';

export type ZoneColorState = {
  id: string;
  hex: string;
  isGradient?: boolean;
  gradient?: unknown;
  patternURL?: string | null;
};

export type TextureFactoryOptions = {
  domContainer: HTMLElement;
  size?: number;
  snaplineList?: { x1: number; y1: number; x2: number; y2: number }[];
};

export type TextureFactory = ReturnType<typeof createTextureFactory>;

export type DesignZone = {
  id: string;
  name: string;
  hex: string;
};

/** UV-space regions on the 2000² sheet (flipY=false), measured from football-shirt.glb */
export const PATTERN_REGION_RECTS: Record<string, { x: number; y: number; w: number; h: number }> = {
  front: { x: 70, y: 1230, w: 500, h: 680 },
  back: { x: 690, y: 1190, w: 620, h: 720 },
  'sleeve-left': { x: 180, y: 740, w: 280, h: 320 },
  'sleeve-right': { x: 840, y: 740, w: 320, h: 320 },
};

export type PatternLayerInput = {
  id: string;
  region: string;
  src: string;
  kind: 'svg' | 'image';
  opacity: number;
  scale: number;
  rotation: number;
  offsetX: number;
  offsetY: number;
  visible: boolean;
  colorOverrides?: Record<string, string>;
};

/** Re-encode SVG data URL with per-layer colour overrides applied. */
function resolvePatternSrc(src: string, overrides?: Record<string, string>): string {
  if (!overrides || !Object.keys(overrides).length) return src;
  const text = decodeSvgDataUrl(src);
  if (text == null) return src;
  const recolored = applySvgColorOverrides(text, overrides);
  return recolored === text ? src : encodeSvgDataUrl(recolored);
}

/**
 * Minimal TextureFactory (M2 spike):
 * SVG template → SVG DOM → Canvas2D raster → THREE.CanvasTexture
 * Color-ID canvas for click-to-zone picking.
 */
export function createTextureFactory(opts: TextureFactoryOptions) {
  const size = opts.size ?? 2000;
  const listeners = {
    templateLoaded: new Set<(url: string) => void>(),
    templateUnloaded: new Set<() => void>(),
    colorAreaClicked: new Set<(zoneId: string) => void>(),
    changed: new Set<() => void>(),
  };

  // Master raster canvas
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Color-ID canvas (zone picking)
  const idCanvas = document.createElement('canvas');
  idCanvas.width = idCanvas.height = size;
  const idCtx = idCanvas.getContext('2d', { willReadFrequently: true })!;
  const colorMap = new Map<string, string>(); // "r,g,b" → zoneId

  const texture = new THREE.CanvasTexture(canvas);
  texture.flipY = false;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  // Hidden live SVG (must be in DOM for getScreenCTM / fonts).
  // Zero-size + clip so the UV sheet never shows as a second "flat" garment under the app.
  const svgHost = opts.domContainer;
  svgHost.style.position = 'absolute';
  svgHost.style.left = '0';
  svgHost.style.top = '0';
  svgHost.style.width = '0';
  svgHost.style.height = '0';
  svgHost.style.overflow = 'hidden';
  svgHost.style.pointerEvents = 'none';
  svgHost.style.opacity = '0';
  svgHost.setAttribute('aria-hidden', 'true');

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('xmlns', NS);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svgHost.appendChild(svg);

  const background = document.createElementNS(NS, 'rect');
  background.setAttribute('id', 'background');
  background.setAttribute('width', String(size));
  background.setAttribute('height', String(size));
  background.setAttribute('fill', '#ffffff');
  svg.appendChild(background);

  const defs = document.createElementNS(NS, 'defs');
  defs.setAttribute('id', 'texture-defs');
  svg.appendChild(defs);

  const designG = document.createElementNS(NS, 'g');
  designG.setAttribute('id', 'design');
  svg.appendChild(designG);

  /** Zone-masked user art — sits above template fills, below free objects (text/print) */
  const zoneArtG = document.createElementNS(NS, 'g');
  zoneArtG.setAttribute('id', 'zone-art');
  svg.appendChild(zoneArtG);

  /** Multi-region pattern layers (front/back/sleeves/…) */
  const patternG = document.createElementNS(NS, 'g');
  patternG.setAttribute('id', 'pattern-layers');
  svg.appendChild(patternG);

  const objectsG = document.createElementNS(NS, 'g');
  objectsG.setAttribute('id', 'objects');
  svg.appendChild(objectsG);

  // snaplines (visual only in SVG space — hidden host)
  const snapG = document.createElementNS(NS, 'g');
  snapG.setAttribute('id', 'snaplines');
  svg.appendChild(snapG);
  for (const line of opts.snaplineList ?? []) {
    const el = document.createElementNS(NS, 'line');
    el.setAttribute('x1', String(line.x1));
    el.setAttribute('y1', String(line.y1));
    el.setAttribute('x2', String(line.x2));
    el.setAttribute('y2', String(line.y2));
    el.setAttribute('stroke', '#00c8ff');
    el.setAttribute('stroke-width', '1');
    el.setAttribute('stroke-dasharray', '8,5');
    el.setAttribute('opacity', '0');
    snapG.appendChild(el);
  }

  let currentTemplateUrl: string | null = null;
  let colorState: Record<string, ZoneColorState> = {};
  let zoneArt: Record<string, string> = {};
  let patternLayers: PatternLayerInput[] = [];
  let rafPending = false;
  let lastRasterMs = 0;

  function emit(key: 'templateLoaded', url: string): void;
  function emit(key: 'templateUnloaded' | 'changed'): void;
  function emit(key: 'colorAreaClicked', zoneId: string): void;
  function emit(key: keyof typeof listeners, ...args: unknown[]) {
    for (const fn of listeners[key]) {
      (fn as (...a: unknown[]) => void)(...args);
    }
  }

  function scheduleRaster() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      rasterize();
    });
  }

  /** Replay SVG DOM → canvas → GPU texture */
  function rasterize() {
    const t0 = performance.now();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    const walk = (root: Element) => {
      for (const child of Array.from(root.children)) {
        const el = child as SVGElement;
        if (el.getAttribute('visibility') === 'hidden') continue;
        const tag = el.tagName.toLowerCase();

        ctx.save();
        // transform
        const t = el.getAttribute('transform');
        if (t) applyTransformAttr(ctx, t);

        // Multiply down the tree (SVG opacity semantics). Do NOT reset to 1
        // when the attribute is missing — that wipes parent layer opacity.
        if (el.hasAttribute('opacity')) {
          const opacity = parseFloat(el.getAttribute('opacity') || '1');
          if (!Number.isNaN(opacity)) {
            const parent = Number.isFinite(ctx.globalAlpha) ? ctx.globalAlpha : 1;
            ctx.globalAlpha = Math.max(0, Math.min(1, parent * opacity));
          }
        }

        const draw = () => {
          if (tag === 'g' || tag === 'svg') {
            walk(el);
          } else if (tag === 'path' || tag === 'polygon' || tag === 'polyline' || tag === 'rect' || tag === 'circle' || tag === 'ellipse' || tag === 'line') {
            drawShape(ctx, el);
          } else if (tag === 'text') {
            drawText(ctx, el);
          } else if (tag === 'image') {
            drawImageEl(ctx, el);
          }
        };
        applyClipAttr(ctx, el, draw);
        ctx.restore();
      }
    };

    walk(svg);
    texture.needsUpdate = true;
    lastRasterMs = performance.now() - t0;
    emit('changed');
  }

  function applyClipAttr(c: CanvasRenderingContext2D, el: Element, draw: () => void) {
    const raw = el.getAttribute('clip-path') || el.getAttribute('clipPath');
    if (!raw) {
      draw();
      return;
    }
    const m = /url\(["']?#([^"')]+)["']?\)/.exec(raw);
    if (!m) {
      draw();
      return;
    }
    const clipEl = defs.querySelector(`#${CSS.escape(m[1])}`);
    if (!clipEl) {
      draw();
      return;
    }
    const path = new Path2D();
    collectClipShapes(clipEl, new DOMMatrix(), path);
    c.save();
    c.clip(path);
    draw();
    c.restore();
  }

  function collectClipShapes(node: Element, matrix: DOMMatrix, into: Path2D) {
    for (const child of Array.from(node.children)) {
      const ct = child.tagName.toLowerCase();
      const local = matrix.multiply(domMatrixFromAttr(child.getAttribute('transform')));
      if (ct === 'path' || ct === 'polygon' || ct === 'polyline') {
        const d = child.getAttribute('d');
        if (d) into.addPath(new Path2D(d), local);
      } else if (ct === 'rect') {
        const x = +(child.getAttribute('x') || 0);
        const y = +(child.getAttribute('y') || 0);
        const w = +(child.getAttribute('width') || 0);
        const h = +(child.getAttribute('height') || 0);
        into.addPath(new Path2D(`M${x},${y}h${w}v${h}h${-w}Z`), local);
      } else if (ct === 'circle') {
        const cx = +(child.getAttribute('cx') || 0);
        const cy = +(child.getAttribute('cy') || 0);
        const r = +(child.getAttribute('r') || 0);
        into.addPath(new Path2D(`M${cx - r},${cy}a${r},${r} 0 1,0 ${r * 2},0a${r},${r} 0 1,0 ${-r * 2},0`), local);
      } else if (ct === 'g' || ct === 'svg' || ct === 'clipPath') {
        collectClipShapes(child, local, into);
      }
    }
  }

  function domMatrixFromAttr(raw: string | null): DOMMatrix {
    const m = new DOMMatrix();
    if (!raw) return m;
    const re = /(translate|scale|rotate|matrix)\s*\(([^)]*)\)/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(raw))) {
      const nums = match[2].split(/[\s,]+/).filter(Boolean).map(Number);
      switch (match[1]) {
        case 'translate':
          m.translateSelf(nums[0] ?? 0, nums[1] ?? 0);
          break;
        case 'scale':
          m.scaleSelf(nums[0] ?? 1, nums[1] ?? nums[0] ?? 1);
          break;
        case 'rotate': {
          const a = nums[0] ?? 0;
          if (nums.length >= 3) {
            m.translateSelf(nums[1], nums[2]);
            m.rotateSelf(a);
            m.translateSelf(-nums[1], -nums[2]);
          } else {
            m.rotateSelf(a);
          }
          break;
        }
        case 'matrix':
          if (nums.length >= 6) {
            const [a, b, c, d, e, f] = nums;
            m.multiplySelf(new DOMMatrix([a, b, c, d, e, f]));
          }
          break;
      }
    }
    return m;
  }

  function applyTransformAttr(c: CanvasRenderingContext2D, raw: string) {
    // support: translate(x y), scale(s), rotate(a cx cy), matrix(...)
    const re = /(translate|scale|rotate|matrix)\s*\(([^)]*)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(raw))) {
      const nums = m[2].split(/[\s,]+/).filter(Boolean).map(Number);
      switch (m[1]) {
        case 'translate':
          c.translate(nums[0] ?? 0, nums[1] ?? 0);
          break;
        case 'scale':
          c.scale(nums[0] ?? 1, nums[1] ?? nums[0] ?? 1);
          break;
        case 'rotate': {
          const a = ((nums[0] ?? 0) * Math.PI) / 180;
          if (nums.length >= 3) {
            c.translate(nums[1], nums[2]);
            c.rotate(a);
            c.translate(-nums[1], -nums[2]);
          } else {
            c.rotate(a);
          }
          break;
        }
        case 'matrix':
          c.transform(...(nums as [number, number, number, number, number, number]));
          break;
      }
    }
  }

  function fillStyleFrom(el: Element): string | CanvasGradient | null {
    const fill = el.getAttribute('fill');
    if (!fill || fill === 'none') return null;
    if (fill.startsWith('url(')) {
      // pattern/gradient refs — resolve simple defs
      const idMatch = fill.match(/url\(['"]?#([^'")]+)['"]?\)/);
      if (idMatch) {
        const ref = svg.querySelector(`#${CSS.escape(idMatch[1])}`);
        if (ref) {
          const tag = ref.tagName.toLowerCase();
          if (tag === 'lineargradient' || tag === 'radialgradient') {
            return makeCanvasGradient(ref, el);
          }
          if (tag === 'pattern') {
            return ref.getAttribute('fill') || '#888888';
          }
        }
      }
      return '#888888';
    }
    return fill;
  }

  function makeCanvasGradient(grad: Element, shape: Element): CanvasGradient | null {
    const stops = Array.from(grad.querySelectorAll('stop'));
    if (!stops.length) return null;
    const isRadial = grad.tagName.toLowerCase() === 'radialgradient';
    let g: CanvasGradient;
    try {
      if (isRadial) {
        g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      } else {
        const angle = parseFloat(grad.getAttribute('data-angle') ?? '90');
        const rad = (angle * Math.PI) / 180;
        const cx = size / 2;
        const cy = size / 2;
        const r = size / 2;
        g = ctx.createLinearGradient(
          cx - Math.cos(rad) * r,
          cy - Math.sin(rad) * r,
          cx + Math.cos(rad) * r,
          cy + Math.sin(rad) * r,
        );
      }
    } catch {
      return null;
    }
    for (const s of stops) {
      const offset = s.getAttribute('offset') ?? '0%';
      const off = offset.endsWith('%') ? parseFloat(offset) / 100 : parseFloat(offset);
      const color =
        s.getAttribute('stop-color') ||
        (s as SVGElement).style?.stopColor ||
        '#000';
      const stopOpacity = s.getAttribute('stop-opacity');
      if (stopOpacity && stopOpacity !== '1') {
        const n = parseFloat(stopOpacity);
        g.addColorStop(off, withAlpha(color, n));
      } else {
        g.addColorStop(off, color);
      }
    }
    void shape;
    return g;
  }

  function withAlpha(hex: string, a: number): string {
    if (hex.startsWith('#') && (hex.length === 7 || hex.length === 4)) {
      const full =
        hex.length === 4
          ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
          : hex;
      const r = parseInt(full.slice(1, 3), 16);
      const g = parseInt(full.slice(3, 5), 16);
      const b = parseInt(full.slice(5, 7), 16);
      return `rgba(${r},${g},${b},${a})`;
    }
    return hex;
  }

  function drawShape(c: CanvasRenderingContext2D, el: Element) {
    const tag = el.tagName.toLowerCase();
    let path: Path2D | null = null;

    if (tag === 'path') {
      const d = el.getAttribute('d');
      if (d) path = new Path2D(d);
    } else if (tag === 'rect') {
      path = new Path2D();
      const x = +(el.getAttribute('x') || 0);
      const y = +(el.getAttribute('y') || 0);
      const w = +(el.getAttribute('width') || 0);
      const h = +(el.getAttribute('height') || 0);
      const rx = +(el.getAttribute('rx') || 0);
      if (rx > 0) path.roundRect(x, y, w, h, rx);
      else path.rect(x, y, w, h);
    } else if (tag === 'circle') {
      path = new Path2D();
      path.arc(+(el.getAttribute('cx') || 0), +(el.getAttribute('cy') || 0), +(el.getAttribute('r') || 0), 0, Math.PI * 2);
    } else if (tag === 'ellipse') {
      path = new Path2D();
      path.ellipse(
        +(el.getAttribute('cx') || 0),
        +(el.getAttribute('cy') || 0),
        +(el.getAttribute('rx') || 0),
        +(el.getAttribute('ry') || 0),
        0,
        0,
        Math.PI * 2,
      );
    } else if (tag === 'polygon' || tag === 'polyline') {
      const pts = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number);
      path = new Path2D();
      if (pts.length >= 2) {
        path.moveTo(pts[0], pts[1]);
        for (let i = 2; i < pts.length; i += 2) path.lineTo(pts[i], pts[i + 1]);
        if (tag === 'polygon') path.closePath();
      }
    } else if (tag === 'line') {
      path = new Path2D();
      path.moveTo(+(el.getAttribute('x1') || 0), +(el.getAttribute('y1') || 0));
      path.lineTo(+(el.getAttribute('x2') || 0), +(el.getAttribute('y2') || 0));
    }

    if (!path) return;

    const fill = fillStyleFrom(el);
    if (fill) {
      c.fillStyle = fill;
      c.fill(path);
    }

    const stroke = el.getAttribute('stroke');
    if (stroke && stroke !== 'none') {
      c.strokeStyle = stroke;
      c.lineWidth = +(el.getAttribute('stroke-width') || 1);
      c.lineCap = 'butt';
      c.stroke(path);
    }
  }

  function drawText(c: CanvasRenderingContext2D, el: Element) {
    const content = el.textContent || '';
    if (!content) return;
    const x = +(el.getAttribute('x') || 0);
    const y = +(el.getAttribute('y') || 0);
    const fontSize = el.getAttribute('font-size') || el.getAttribute('fontSize') || '48';
    const fontFamily = (el.getAttribute('font-family') || el.getAttribute('fontFamily') || 'Inter, sans-serif').replace(/["']/g, '');
    const weight = el.getAttribute('font-weight') || '700';
    c.font = `${weight} ${fontSize}px ${fontFamily}`;
    // SVG text-anchor uses middle/start/end; CanvasTextAlign uses center/left/right
    const anchor = el.getAttribute('text-anchor') || 'middle';
    c.textAlign = anchor === 'middle' ? 'center' : anchor === 'start' ? 'left' : anchor === 'end' ? 'right' : 'center';
    c.textBaseline = 'middle';

    const fill = fillStyleFrom(el) ?? el.getAttribute('fill');
    const stroke = el.getAttribute('stroke');
    const sw = +(el.getAttribute('stroke-width') || 0);

    if (stroke && stroke !== 'none' && sw > 0) {
      c.strokeStyle = stroke;
      c.lineWidth = sw;
      c.strokeText(content, x, y);
    }
    if (fill) {
      c.fillStyle = fill;
      c.fillText(content, x, y);
    }
  }

  function drawImageEl(c: CanvasRenderingContext2D, el: Element) {
    const href = el.getAttribute('href') || el.getAttribute('xlink:href');
    if (!href) return;
    const x = +(el.getAttribute('x') || 0);
    const y = +(el.getAttribute('y') || 0);
    const w = +(el.getAttribute('width') || 0);
    const h = +(el.getAttribute('height') || 0);
    type ImgEl = SVGImageElement & { __img?: HTMLImageElement };
    const imgEl = el as ImgEl;
    const img = imgEl.__img;
    // CSS filter (tint) is not applied by drawImage — bake a simple tint if set
    function paint(src: CanvasImageSource) {
      c.drawImage(src, x, y, w, h);
    };
    if (img?.complete && w && h) {
      paint(img);
    } else if (href && w && h) {
      const image = new Image();
      image.onload = () => {
        imgEl.__img = image;
        scheduleRaster();
      };
      image.src = href;
    }
  }

  function paintColorIdCanvas() {
    idCtx.clearRect(0, 0, size, size);
    colorMap.clear();
    const paths = designG.querySelectorAll('path[id], rect[id], polygon[id]');
    const rootCTM = svg.getScreenCTM();
    let i = 0;
    paths.forEach(pathEl => {
      const id = pathEl.getAttribute('id');
      if (!id) return;
      i += 1;
      const r = (73 * i) & 255 || 1;
      const g = (149 * i) & 255;
      const b = (211 * i) & 255;
      colorMap.set(`${r},${g},${b}`, id);

      idCtx.save();
      if (rootCTM && pathEl instanceof SVGGraphicsElement) {
        const ctm = pathEl.getScreenCTM();
        if (ctm) {
          const inv = rootCTM.inverse();
          const m = inv.multiply(ctm);
          idCtx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
        }
      }
      idCtx.fillStyle = `rgb(${r},${g},${b})`;
      const d = pathEl.getAttribute('d');
      if (d) idCtx.fill(new Path2D(d));
      else if (pathEl.tagName.toLowerCase() === 'rect') {
        const x = +(pathEl.getAttribute('x') || 0);
        const y = +(pathEl.getAttribute('y') || 0);
        const w = +(pathEl.getAttribute('width') || 0);
        const h = +(pathEl.getAttribute('height') || 0);
        idCtx.fillRect(x, y, w, h);
      }
      idCtx.restore();
    });
  }

  async function loadTemplate(url: string) {
    const t0 = performance.now();
    console.info('[texture] loadTemplate start', url);
    if (currentTemplateUrl) {
      designG.replaceChildren();
      emit('templateUnloaded');
    }
    // Zone art references template paths — drop clips/art with the old template
    zoneArtG.replaceChildren();
    patternG.replaceChildren();
    defs.replaceChildren();
    zoneArt = {};
    // patternLayers kept in memory; re-applied by store after loadTemplate
    const res = await fetch(url, { signal: AbortSignal.timeout(20_000) });
    console.info('[texture] fetch status', res.status, url);
    if (!res.ok) throw new Error(`Failed to load template ${url}`);
    const text = await res.text();
    console.info('[texture] text read', { bytes: text.length });
    const tParse = performance.now();
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const root = doc.documentElement;
    if (root.nodeName === 'parsererror' || root.querySelector('parsererror')) {
      throw new Error(`Invalid SVG template: ${url}`);
    }
    console.info('[texture] parsed', { root: root.nodeName });

    designG.replaceChildren();
    // importNode copies — must detach from root or firstChild never advances
    const nodes = Array.from(root.childNodes);
    for (const node of nodes) {
      designG.appendChild(document.importNode(node, true));
    }
    console.info('[texture] imported', { topNodes: nodes.length });

    currentTemplateUrl = url;
    console.info('[texture] paint color-id…', { bytes: text.length, topNodes: nodes.length });
    paintColorIdCanvas();
    console.info('[texture] rasterize…', { afterColorIdMs: Math.round(performance.now() - tParse) });
    rasterize();
    emit('templateLoaded', url);
    console.info('[texture] loadTemplate done', {
      totalMs: Math.round(performance.now() - t0),
      rasterMs: lastRasterMs,
      zones: getZones().length,
    });
  }

  function setTemplateColor({ id, hex }: { id: string; hex: string }) {
    const el = designG.querySelector(`[id="${CSS.escape(id)}"]`);
    if (el) {
      el.setAttribute('fill', hex);
      colorState[id] = { id, hex };
      scheduleRaster();
    }
  }

  /**
   * Attach a user image masked to a zone path (Base, Arms, …).
   * Pass null to clear art for that zone.
   */
  function setZoneArt(zoneId: string, dataUrl: string | null) {
    const clipId = `zone-clip-${zoneId}`;
    const existing = zoneArtG.querySelector(
      `[data-zone="${CSS.escape(zoneId)}"]`,
    );
    if (existing) existing.remove();
    const oldClip = defs.querySelector(`#${CSS.escape(clipId)}`);
    if (oldClip) oldClip.remove();

    if (!dataUrl) {
      delete zoneArt[zoneId];
      scheduleRaster();
      return;
    }

    const zoneEl = designG.querySelector(`[id="${CSS.escape(zoneId)}"]`);
    if (!zoneEl || !(zoneEl instanceof SVGGraphicsElement)) return;

    // Bake zone path (with ancestor transforms) into a clipPath in root user space
    const clipPath = document.createElementNS(NS, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');

    const d = zoneEl.getAttribute('d');
    if (d) {
      const clone = document.createElementNS(NS, 'path');
      clone.setAttribute('d', d);
      const ctm = zoneEl.getCTM();
      if (ctm) {
        clone.setAttribute(
          'transform',
          `matrix(${ctm.a} ${ctm.b} ${ctm.c} ${ctm.d} ${ctm.e} ${ctm.f})`,
        );
      }
      clipPath.appendChild(clone);
    } else if (zoneEl.tagName.toLowerCase() === 'rect') {
      const clone = document.createElementNS(NS, 'rect');
      for (const attr of ['x', 'y', 'width', 'height'] as const) {
        const v = zoneEl.getAttribute(attr);
        if (v) clone.setAttribute(attr, v);
      }
      const ctm = zoneEl.getCTM();
      if (ctm) {
        clone.setAttribute(
          'transform',
          `matrix(${ctm.a} ${ctm.b} ${ctm.c} ${ctm.d} ${ctm.e} ${ctm.f})`,
        );
      }
      clipPath.appendChild(clone);
    } else {
      return;
    }
    defs.appendChild(clipPath);

    // Fit image to zone bbox in root user space
    const bb = zoneEl.getBBox();
    const corners: DOMPoint[] = [
      new DOMPoint(bb.x, bb.y),
      new DOMPoint(bb.x + bb.width, bb.y),
      new DOMPoint(bb.x, bb.y + bb.height),
      new DOMPoint(bb.x + bb.width, bb.y + bb.height),
    ];
    const ctm = zoneEl.getCTM();
    const mapped = ctm ? corners.map(p => p.matrixTransform(ctm)) : (corners as unknown as DOMPoint[]);
    const xs = mapped.map(p => p.x);
    const ys = mapped.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const w = Math.max(...xs) - minX;
    const h = Math.max(...ys) - minY;

    const g = document.createElementNS(NS, 'g');
    g.setAttribute('data-zone', zoneId);
    g.setAttribute('clip-path', `url(#${clipId})`);

    const img = document.createElementNS(NS, 'image');
    img.setAttribute('href', dataUrl);
    img.setAttribute('x', String(minX));
    img.setAttribute('y', String(minY));
    img.setAttribute('width', String(w));
    img.setAttribute('height', String(h));
    img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    g.appendChild(img);
    zoneArtG.appendChild(g);

    zoneArt[zoneId] = dataUrl;
    scheduleRaster();
  }

  function getZoneArt(): Record<string, string> {
    return { ...zoneArt };
  }

  function zoneBoundsInRoot(zoneEl: SVGGraphicsElement) {
    const bb = zoneEl.getBBox();
    const corners: DOMPoint[] = [
      new DOMPoint(bb.x, bb.y),
      new DOMPoint(bb.x + bb.width, bb.y),
      new DOMPoint(bb.x, bb.y + bb.height),
      new DOMPoint(bb.x + bb.width, bb.y + bb.height),
    ];
    const ctm = zoneEl.getCTM();
    const mapped = ctm ? corners.map(p => p.matrixTransform(ctm)) : corners;
    const xs = mapped.map(p => p.x);
    const ys = mapped.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY, ctm };
  }

  function bakeZoneClip(zoneEl: SVGGraphicsElement, clipId: string): boolean {
    const old = defs.querySelector(`#${CSS.escape(clipId)}`);
    if (old) old.remove();
    const clipPath = document.createElementNS(NS, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const d = zoneEl.getAttribute('d');
    if (d) {
      const clone = document.createElementNS(NS, 'path');
      clone.setAttribute('d', d);
      const ctm = zoneEl.getCTM();
      if (ctm) {
        clone.setAttribute('transform', `matrix(${ctm.a} ${ctm.b} ${ctm.c} ${ctm.d} ${ctm.e} ${ctm.f})`);
      }
      clipPath.appendChild(clone);
    } else if (zoneEl.tagName.toLowerCase() === 'rect') {
      const clone = document.createElementNS(NS, 'rect');
      for (const attr of ['x', 'y', 'width', 'height'] as const) {
        const v = zoneEl.getAttribute(attr);
        if (v) clone.setAttribute(attr, v);
      }
      const ctm = zoneEl.getCTM();
      if (ctm) {
        clone.setAttribute('transform', `matrix(${ctm.a} ${ctm.b} ${ctm.c} ${ctm.d} ${ctm.e} ${ctm.f})`);
      }
      clipPath.appendChild(clone);
    } else {
      return false;
    }
    defs.appendChild(clipPath);
    return true;
  }

  function findZoneEl(region: string): SVGGraphicsElement | null {
    const bare = region.startsWith('zone:') ? region.slice(5) : region;
    const candidates = [
      bare,
      bare.toLowerCase(),
      bare.toUpperCase(),
      bare.charAt(0).toUpperCase() + bare.slice(1),
      bare === 'collar' ? 'Collar' : '',
      bare === 'cuff' ? 'Cuff' : '',
      bare === 'hem' ? 'Hem' : '',
      bare === 'placket' ? 'Placket' : '',
      bare === 'sleeve-left' || bare === 'sleeve-right' ? 'sleeves' : '',
      bare === 'sleeve-left' || bare === 'sleeve-right' ? 'Arms' : '',
      bare === 'sleeve-left' || bare === 'sleeve-right' ? 'Sleeve' : '',
    ].filter(Boolean);
    for (const id of candidates) {
      const el = designG.querySelector(`[id="${CSS.escape(id)}"]`);
      if (el instanceof SVGGraphicsElement) return el;
    }
    return null;
  }

  function rectClip(clipId: string, r: { x: number; y: number; w: number; h: number }) {
    const old = defs.querySelector(`#${CSS.escape(clipId)}`);
    if (old) old.remove();
    const clipPath = document.createElementNS(NS, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const rect = document.createElementNS(NS, 'rect');
    rect.setAttribute('x', String(r.x));
    rect.setAttribute('y', String(r.y));
    rect.setAttribute('width', String(r.w));
    rect.setAttribute('height', String(r.h));
    clipPath.appendChild(rect);
    defs.appendChild(clipPath);
  }

  /**
   * Rebuild all pattern layers (front / back / sleeves / zone pieces).
   * SVG sources are full UV designs clipped to the region; image sources cover the region box.
   */
  function setPatternLayers(layers: PatternLayerInput[]) {
    patternLayers = layers.map(l => ({ ...l }));
    patternG.replaceChildren();
    // Drop only pattern-* clips (leave zone-art clips alone)
    defs.querySelectorAll('clipPath[id^="pat-"]').forEach(el => el.remove());

    for (const layer of patternLayers) {
      if (!layer.visible || !layer.src) continue;
      const clipId = `pat-${layer.id.replace(/[^\w-]/g, '_')}`;
      let clipBox: { x: number; y: number; w: number; h: number };

      const rect = PATTERN_REGION_RECTS[layer.region];
      const zoneEl = rect ? null : findZoneEl(layer.region);
      if (zoneEl) {
        if (!bakeZoneClip(zoneEl, clipId)) continue;
        clipBox = zoneBoundsInRoot(zoneEl);
      } else if (rect) {
        rectClip(clipId, rect);
        clipBox = rect;
      } else {
        // fallback: full sheet
        rectClip(clipId, { x: 0, y: 0, w: size, h: size });
        clipBox = { x: 0, y: 0, w: size, h: size };
      }

      const g = document.createElementNS(NS, 'g');
      g.setAttribute('data-layer', layer.id);
      g.setAttribute('data-region', layer.region);
      g.setAttribute('clip-path', `url(#${clipId})`);
      if (layer.opacity < 1) g.setAttribute('opacity', String(layer.opacity));

      const img = document.createElementNS(NS, 'image');
      img.setAttribute('href', resolvePatternSrc(layer.src, layer.colorOverrides));

      if (layer.kind === 'svg') {
        // Full UV sheet; transform for scale/rotate/offset inside the clip
        img.setAttribute('x', '0');
        img.setAttribute('y', '0');
        img.setAttribute('width', String(size));
        img.setAttribute('height', String(size));
        const cx = clipBox.x + clipBox.w / 2;
        const cy = clipBox.y + clipBox.h / 2;
        const ox = layer.offsetX * clipBox.w * 0.5;
        const oy = layer.offsetY * clipBox.h * 0.5;
        const parts: string[] = [];
        if (layer.rotation) parts.push(`rotate(${layer.rotation} ${cx} ${cy})`);
        if (layer.scale !== 1) parts.push(`translate(${cx} ${cy}) scale(${layer.scale}) translate(${-cx} ${-cy})`);
        if (ox || oy) parts.push(`translate(${ox} ${oy})`);
        if (parts.length) img.setAttribute('transform', parts.join(' '));
      } else {
        // Image: cover region box with fit + transforms
        const s = layer.scale;
        const w = clipBox.w * s;
        const h = clipBox.h * s;
        const cx = clipBox.x + clipBox.w / 2 + layer.offsetX * clipBox.w * 0.5;
        const cy = clipBox.y + clipBox.h / 2 + layer.offsetY * clipBox.h * 0.5;
        img.setAttribute('x', String(cx - w / 2));
        img.setAttribute('y', String(cy - h / 2));
        img.setAttribute('width', String(w));
        img.setAttribute('height', String(h));
        img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        if (layer.rotation) {
          img.setAttribute('transform', `rotate(${layer.rotation} ${cx} ${cy})`);
        }
      }
      g.appendChild(img);
      patternG.appendChild(g);
    }
    scheduleRaster();
  }

  function getPatternLayers(): PatternLayerInput[] {
    return patternLayers.map(l => ({ ...l }));
  }

  function getZones(): DesignZone[] {
    const byId = new Map<string, DesignZone>();
    designG.querySelectorAll('[class~="color"], .editable-color').forEach(el => {
      const id = el.getAttribute('id');
      if (!id || byId.has(id)) return;
      byId.set(id, {
        id,
        name: el.getAttribute('data-title') || id,
        hex: (el.getAttribute('fill') || '#000000').toLowerCase(),
      });
    });
    if (!byId.size) {
      designG.querySelectorAll('path[id]').forEach(el => {
        const id = el.getAttribute('id');
        if (!id || byId.has(id)) return;
        byId.set(id, {
          id,
          name: el.getAttribute('data-title') || id,
          hex: (el.getAttribute('fill') || '#000000').toLowerCase(),
        });
      });
    }
    return [...byId.values()];
  }

  function pickZone(uvX: number, uvY: number): string | null {
    const px = Math.floor(uvX * size);
    const py = Math.floor(uvY * size);
    if (px < 0 || py < 0 || px >= size || py >= size) return null;
    const data = idCtx.getImageData(px, py, 1, 1).data;
    if (data[3] === 0) return null;
    const id = colorMap.get(`${data[0]},${data[1]},${data[2]}`) ?? null;
    if (id) emit('colorAreaClicked', id);
    return id;
  }

  function addText(
    text: string,
    x: number,
    y: number,
    style: Partial<{
      fontFamily: string;
      fontSize: string;
      fill: string;
      stroke: string;
      strokeWidth: number;
      rotation: number;
      visible: boolean;
      role: string;
    }> = {},
  ) {
    const el = document.createElementNS(NS, 'text');
    el.setAttribute('x', String(x));
    el.setAttribute('y', String(y));
    el.setAttribute('text-anchor', 'middle');
    el.setAttribute('dominant-baseline', 'middle');
    el.setAttribute('font-size', style.fontSize ?? '96');
    el.setAttribute('font-family', style.fontFamily ?? 'Oswald, Inter, sans-serif');
    el.setAttribute('font-weight', '700');
    el.setAttribute('fill', style.fill ?? '#ffffff');
    if (style.stroke) {
      el.setAttribute('stroke', style.stroke);
      el.setAttribute('stroke-width', String(style.strokeWidth ?? 5));
      el.setAttribute('paint-order', 'stroke');
    }
    if (style.rotation) {
      el.setAttribute('transform', `rotate(${style.rotation} ${x} ${y})`);
    }
    if (style.visible === false) el.setAttribute('visibility', 'hidden');
    el.textContent = text;
    el.dataset.uuid = `T-${crypto.randomUUID()}`;
    if (style.role) el.dataset.role = style.role;
    objectsG.appendChild(el);
    scheduleRaster();
    return el.dataset.uuid;
  }

  function addImage(
    dataUrl: string,
    x: number,
    y: number,
    w: number,
    h: number,
    rotation = 0,
    opacity = 1,
    role?: string,
  ) {
    const el = document.createElementNS(NS, 'image');
    el.setAttribute('href', dataUrl);
    el.setAttribute('x', String(x - w / 2));
    el.setAttribute('y', String(y - h / 2));
    el.setAttribute('width', String(w));
    el.setAttribute('height', String(h));
    if (rotation) el.setAttribute('transform', `rotate(${rotation} ${x} ${y})`);
    if (opacity < 1) el.setAttribute('opacity', String(opacity));
    el.dataset.uuid = `I-${crypto.randomUUID()}`;
    if (role) el.dataset.role = role;
    objectsG.appendChild(el);
    scheduleRaster();
    return el.dataset.uuid as string;
  }

  function updateImage(
    uuid: string,
    patch: {
      x?: number;
      y?: number;
      w?: number;
      h?: number;
      rotation?: number;
      opacity?: number;
    },
  ) {
    const el = objectsG.querySelector(`[data-uuid="${CSS.escape(uuid)}"]`) as SVGImageElement | null;
    if (!el) return false;
    const w = Number(el.getAttribute('width')) || 100;
    const h = Number(el.getAttribute('height')) || 100;
    const cx = Number(el.getAttribute('x')) + w / 2;
    const cy = Number(el.getAttribute('y')) + h / 2;
    const nextX = patch.x ?? cx;
    const nextY = patch.y ?? cy;
    const nextW = patch.w ?? w;
    const nextH = patch.h ?? h;
    const nextRot =
      patch.rotation ??
      Number((el.getAttribute('transform') || '').replace(/.*rotate\(([-\d.]+).*/, '$1') || 0);
    el.setAttribute('x', String(nextX - nextW / 2));
    el.setAttribute('y', String(nextY - nextH / 2));
    el.setAttribute('width', String(nextW));
    el.setAttribute('height', String(nextH));
    if (nextRot) el.setAttribute('transform', `rotate(${nextRot} ${nextX} ${nextY})`);
    else el.removeAttribute('transform');
    if (patch.opacity !== undefined) {
      if (patch.opacity < 1) el.setAttribute('opacity', String(patch.opacity));
      else el.removeAttribute('opacity');
    }
    scheduleRaster();
    return true;
  }

  function removeObject(uuid: string) {
    const el = objectsG.querySelector(`[data-uuid="${CSS.escape(uuid)}"]`);
    if (!el) return false;
    el.remove();
    scheduleRaster();
    return true;
  }

  function clearObjects() {
    objectsG.replaceChildren();
    scheduleRaster();
  }

  /** Dashed selection frame on the texture (editing only — strip before capture) */
  function addHighlight(x: number, y: number, w: number, h: number, rotation = 0) {
    const pad = 10;
    const el = document.createElementNS(NS, 'rect');
    el.setAttribute('x', String(x - w / 2 - pad));
    el.setAttribute('y', String(y - h / 2 - pad));
    el.setAttribute('width', String(w + pad * 2));
    el.setAttribute('height', String(h + pad * 2));
    el.setAttribute('rx', '6');
    el.setAttribute('fill', 'none');
    el.setAttribute('stroke', '#0f172a');
    el.setAttribute('stroke-width', '3');
    el.setAttribute('stroke-dasharray', '10 8');
    el.setAttribute('data-ui', 'selection');
    if (rotation) el.setAttribute('transform', `rotate(${rotation} ${x} ${y})`);
    objectsG.appendChild(el);

    // Small dark corner ticks
    const bx = w / 2 + pad;
    const by = h / 2 + pad;
    const arm = Math.min(22, Math.min(w, h) * 0.15 + 8);
    const corners: { x: number; y: number; sx: number; sy: number }[] = [
      { x: x - bx, y: y - by, sx: 1, sy: 1 },
      { x: x + bx, y: y - by, sx: -1, sy: 1 },
      { x: x - bx, y: y + by, sx: 1, sy: -1 },
      { x: x + bx, y: y + by, sx: -1, sy: -1 },
    ];
    for (const c of corners) {
      const path = document.createElementNS(NS, 'path');
      path.setAttribute(
        'd',
        `M ${c.x + c.sx * arm} ${c.y} L ${c.x} ${c.y} L ${c.x} ${c.y + c.sy * arm}`,
      );
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', '#0f172a');
      path.setAttribute('stroke-width', '4');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('data-ui', 'selection');
      if (rotation) path.setAttribute('transform', `rotate(${rotation} ${x} ${y})`);
      objectsG.appendChild(path);
    }

    scheduleRaster();
  }

  /** Strip selection chrome before share/capture raster */
  function clearHighlights() {
    objectsG.querySelectorAll('[data-ui="selection"]').forEach(n => n.remove());
    scheduleRaster();
  }

  /** Hit-test print objects at texture UV (0–1) → returns data-role / data-uuid */
  function pickObject(uvX: number, uvY: number): { uuid: string; role: string | null } | null {
    const px = uvX * size;
    const py = uvY * size;
    for (const child of Array.from(objectsG.children).reverse()) {
      const el = child as SVGElement;
      if (el.getAttribute('visibility') === 'hidden') continue;
      const tag = el.tagName.toLowerCase();
      let hit = false;
      if (tag === 'text') {
        const x = +(el.getAttribute('x') || 0);
        const y = +(el.getAttribute('y') || 0);
        const fs = +(el.getAttribute('font-size') || 48);
        const content = el.textContent || '';
        const halfW = Math.max(content.length * fs * 0.35, fs);
        const halfH = fs * 0.7;
        // rough AABB ignoring rotation
        hit = Math.abs(px - x) <= halfW && Math.abs(py - y) <= halfH;
      } else if (tag === 'image') {
        const x = +(el.getAttribute('x') || 0);
        const y = +(el.getAttribute('y') || 0);
        const w = +(el.getAttribute('width') || 0);
        const h = +(el.getAttribute('height') || 0);
        hit = px >= x && px <= x + w && py >= y && py <= y + h;
      }
      if (hit) {
        return {
          uuid: el.dataset.uuid || '',
          role: el.dataset.role || null,
        };
      }
    }
    return null;
  }

  function toDataURL(): string {
    return canvas.toDataURL('image/png');
  }

  function getSize() {
    return size;
  }

  function getTexture() {
    return texture;
  }

  function getColorState() {
    return Object.values(colorState);
  }

  function getCurrentTemplateUrl() {
    return currentTemplateUrl;
  }

  function getRasterMs() {
    return lastRasterMs;
  }

  return {
    loadTemplate,
    setTemplateColor,
    getZones,
    setZoneArt,
    getZoneArt,
    setPatternLayers,
    getPatternLayers,
    pickZone,
    pickObject,
    addText,
    addImage,
    updateImage,
    removeObject,
    clearObjects,
    addHighlight,
    clearHighlights,
    toDataURL,
    getSize,
    getTexture,
    getColorState,
    getCurrentTemplateUrl,
    getRasterMs,
    scheduleRaster,
    rasterize,
    signals: {
      on: {
        templateLoaded: (fn: (url: string) => void) => listeners.templateLoaded.add(fn),
        templateUnloaded: (fn: () => void) => listeners.templateUnloaded.add(fn),
        colorAreaClicked: (fn: (id: string) => void) => listeners.colorAreaClicked.add(fn),
        changed: (fn: () => void) => listeners.changed.add(fn),
      },
      off: {
        templateLoaded: (fn: (url: string) => void) => listeners.templateLoaded.delete(fn),
        templateUnloaded: (fn: () => void) => listeners.templateUnloaded.delete(fn),
        colorAreaClicked: (fn: (id: string) => void) => listeners.colorAreaClicked.delete(fn),
        changed: (fn: () => void) => listeners.changed.delete(fn),
      },
    },
    host: { svg, designG, zoneArtG, patternG, objectsG, defs, canvas },
  };
}
